import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { Envelope, GameDetail, LiveRefetch, LiveServers } from '../types';

export type { LiveRefetch };

/**
 * `GET /api/live/servers` - the homepage seed (active servers + activity feed).
 * Echo pushes deltas; `refetchInterval` is the socket-drop fallback.
 */
export function useLiveServers(refetchInterval: LiveRefetch = false): UseQueryResult<LiveServers> {
  return useQuery({
    queryKey: queryKeys.live.servers(),
    refetchInterval,
    queryFn: async () => {
      const res = await api.get<Envelope<LiveServers>>('/live/servers');
      return res.data;
    },
  });
}

/** `GET /api/live/games/{id}` - the live game seed (same shape as game detail). */
export function useLiveGame(
  id: number | undefined,
  refetchInterval: LiveRefetch = false,
): UseQueryResult<GameDetail> {
  return useQuery({
    queryKey: queryKeys.live.game(id ?? 0),
    enabled: id !== undefined,
    refetchInterval,
    queryFn: async () => {
      const res = await api.get<Envelope<GameDetail>>(
        `/live/games/${encodeURIComponent(String(id))}`,
      );
      return res.data;
    },
  });
}
